import { useEffect, useRef, useState, Suspense } from 'react';
import { lazyRoute } from '../shared/utils/lazyRoute';
import { createPortal } from 'react-dom';
import { Routes, Route, Navigate } from 'react-router-dom';
import type { Session } from '@supabase/supabase-js';
import * as Sentry from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { supabase } from '../shared/services/supabase';
import { queryClient } from '../shared/services/queryClient';
import { Toaster } from '../shared/components/ui/Toast';
import { AuthForm } from '../auth/components/AuthForm';
import { RegistrationsClosedScreen } from '../auth/components/RegistrationsClosedNotice';
import { OrganizationProvider, useOrganizationContext } from '../shared/contexts/OrganizationContext';
import { GuideCreationProvider } from '../shared/contexts/GuideCreationContext';
import { ErrorBoundary } from '../shared/components/error/ErrorBoundary';
import { OrgGuard } from '../shared/components/layout/OrgGuard';
import { OrgLayout } from '../portals/organization/layout/OrgLayout';
import { AdminLayout } from '../portals/admin/layout/AdminLayout';
import { RoleGuard } from '../shared/components/layout/RoleGuard';
import { usePageViewTracking } from '../shared/hooks/useAnalytics';

// Lazy-load page components to enable route-based code splitting
// All routes use lazyRoute() for consistent handling of chunk load failures
const CreatorLayout = lazyRoute(() => import('../portals/creator/layout/CreatorLayout'));
const CreatorDashboard = lazyRoute(() => import('../portals/creator/pages/DashboardPage'));
const ReportPage = lazyRoute(() => import('../portals/creator/pages/ReportPage'));
const GlobalReportPage = lazyRoute(() => import('../portals/creator/pages/GlobalReportPage'));
const GuidesPage = lazyRoute(() => import('../portals/creator/pages/GuidesPage'));
const CreateGuidePage = lazyRoute(() => import('../portals/creator/pages/CreateGuidePage'));
const EditGuidePage = lazyRoute(() => import('../portals/creator/pages/EditGuidePage'));
const ProfilePage = lazyRoute(() => import('../portals/creator/pages/ProfilePage'));
const GettingStartedPage = lazyRoute(() => import('../portals/creator/pages/GettingStartedPage'));
const CampaignsPage = lazyRoute(() => import('../portals/creator/pages/CampaignsPage'));
const OnboardingPage = lazyRoute(() => import('../portals/creator/pages/OnboardingPage'));
const PortfolioPage = lazyRoute(() => import('../portals/creator/pages/PortfolioPage'));
const EditPortfolioPage = lazyRoute(() => import('../portals/creator/pages/EditPortfolioPage'));
const LinksPage = lazyRoute(() => import('../portals/creator/pages/LinksPage'));
const CreatorActionCenterPage = lazyRoute(() => import('../portals/creator/pages/CreatorActionCenterPage'));
const CreatorHelpPage = lazyRoute(() => import('../portals/creator/pages/CreatorHelpPage'));

const ClientReportPage = lazyRoute(() => import('../domains/reporting/pages/ClientReportPage'));

const AuthCallbackPage = lazyRoute(() => import('../auth/pages/AuthCallbackPage'));
const BrandAuthPage = lazyRoute(() => import('../auth/pages/BrandAuthPage'));
const AgencyAuthPage = lazyRoute(() => import('../auth/pages/AgencyAuthPage'));
const AcceptInvitePage = lazyRoute(() => import('../auth/pages/AcceptInvitePage'));
const TestimonialPage = lazyRoute(() => import('../auth/pages/TestimonialPage'));

// New organisation creation is disabled (CollabsMap transition); the /org/new
// route now renders the role-aware "registrations closed" screen instead of
// CreateOrganizationPage.
const OrgSelectPage = lazyRoute(() => import('../auth/onboarding/OrgSelectPage'));

const OrgDashboardPage = lazyRoute(() => import('../portals/organization/pages/OrgDashboardPage'));
const OrgReportsPage = lazyRoute(() => import('../portals/organization/pages/OrgReportsPage'));
const OrgCampaignsPage = lazyRoute(() => import('../portals/organization/pages/OrgCampaignsPage'));
const OrgClientsPage = lazyRoute(() => import('../portals/organization/pages/OrgClientsPage'));
const OrgCreatorsPage = lazyRoute(() => import('../portals/organization/pages/OrgCreatorsPage'));
const OrgTeamPage = lazyRoute(() => import('../portals/organization/pages/OrgTeamPage'));
const OrgBillingPage = lazyRoute(() => import('../portals/organization/pages/OrgBillingPage'));
const OrgSettingsPage = lazyRoute(() => import('../portals/organization/pages/OrgSettingsPage'));
const OrgActionCenterPage = lazyRoute(() => import('../portals/organization/pages/OrgActionCenterPage'));
const OrgHelpPage = lazyRoute(() => import('../portals/organization/pages/OrgHelpPage'));

const AdminDashboardPage = lazyRoute(() => import('../portals/admin/pages/AdminDashboardPage'));
const AdminUsersPage = lazyRoute(() => import('../portals/admin/pages/AdminUsersPage'));
const AdminOrganizationsPage = lazyRoute(() => import('../portals/admin/pages/AdminOrganizationsPage'));
const AdminCampaignsPage = lazyRoute(() => import('../portals/admin/pages/AdminCampaignsPage'));
const AdminCreatorsPage = lazyRoute(() => import('../portals/admin/pages/AdminCreatorsPage'));
const AdminClientsPage = lazyRoute(() => import('../portals/admin/pages/AdminClientsPage'));
const AdminReportsPage = lazyRoute(() => import('../portals/admin/pages/AdminReportsPage'));
const AdminCompanyStatsPage = lazyRoute(() => import('../portals/admin/pages/AdminCompanyStatsPage'));
const AdminDeliverablesPage = lazyRoute(() => import('../portals/admin/pages/AdminDeliverablesPage'));
const AdminActionCenterPage = lazyRoute(() => import('../portals/admin/pages/AdminActionCenterPage'));
const AdminCreatorOpportunitiesPage = lazyRoute(() => import('../portals/admin/pages/AdminCreatorOpportunitiesPage'));

// Loading spinner component
function LoadingSpinner() {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
  );
}

// Root redirect based on user context
function RootRedirect() {
  const { userContext, isLoading, isSystemAdmin, isOrgMember, isOrgUser } = useOrganizationContext();
  const [profileChecked, setProfileChecked] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  // Check if creator profile needs onboarding
  useEffect(() => {
    const checkProfile = async () => {
      // Only check for creators (not admins, org members, or org-role users)
      if (isLoading || isSystemAdmin || isOrgMember || isOrgUser) {
        setProfileChecked(true);
        return;
      }

      try {
        const profileId = userContext?.profileId;
        if (!profileId) {
          setProfileChecked(true);
          return;
        }

        setNeedsOnboarding(userContext?.updatedUsername !== true);
      } catch {
        // On error, don't block - proceed to dashboard
      }
      setProfileChecked(true);
    };

    checkProfile();
  }, [isLoading, isSystemAdmin, isOrgMember, isOrgUser, userContext?.profileId, userContext?.updatedUsername]);

  if (isLoading || !profileChecked) {
    return <LoadingSpinner />;
  }

  // ── Pending invite acceptance takes top priority ─────────────────────────
  // If the user was in the middle of accepting an invite, always send them
  // back there — regardless of auth_intent or anything else.
  const returnTo = localStorage.getItem('return_to');
  if (returnTo?.startsWith('/auth/invite/')) {
    localStorage.removeItem('return_to');
    localStorage.removeItem('auth_intent');
    return <Navigate to={returnTo} replace />;
  }

  // ── Brand / agency signup intent ─────────────────────────────────────────
  // Set by BrandAuthPage / AgencyAuthPage on mount so we route new sign-ups
  // to /org/new instead of /onboarding (creator flow).
  const authIntent = localStorage.getItem('auth_intent');
  if (authIntent === 'brand' || authIntent === 'agency') {
    localStorage.removeItem('auth_intent');
    localStorage.removeItem('return_to');

    // Existing brand/agency user signing back in — go to their org
    if (isOrgMember && userContext?.organizations.length) {
      if (userContext.organizations.length > 1) {
        return <Navigate to="/org/select" replace />;
      }
      const firstOrg = userContext.organizations[0].organization;
      return <Navigate to={`/org/${firstOrg.slug}/dashboard`} replace />;
    }

    // Creator account accidentally landing here — send to their dashboard
    if (!needsOnboarding) {
      return <Navigate to="/dashboard" replace />;
    }

    // New brand/agency user — create org
    return <Navigate to="/org/new" replace />;
  }

  // Check if we have a return_to path (exclude auth pages, but allow invite paths handled above)
  if (returnTo && returnTo !== '/' && !returnTo.startsWith('/auth') && !returnTo.startsWith('/onboarding')) {
    localStorage.removeItem('return_to');
    // Org-role users should not be sent to creator routes
    if (isOrgUser && !returnTo.startsWith('/org/') && !returnTo.startsWith('/admin/')) {
      // Fall through to org routing below
    } else {
      if (needsOnboarding && !isSystemAdmin && !isOrgMember) {
        return <Navigate to="/onboarding" replace />;
      }
      return <Navigate to={returnTo} replace />;
    }
  }

  // If system admin, redirect to admin dashboard
  if (isSystemAdmin) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  // If user belongs to orgs, redirect to org select or first org
  if (isOrgMember && userContext?.organizations.length) {
    if (userContext.organizations.length > 1) {
      return <Navigate to="/org/select" replace />;
    }
    const firstOrg = userContext.organizations[0].organization;
    return <Navigate to={`/org/${firstOrg.slug}/dashboard`} replace />;
  }

  // For creators: check if onboarding is needed
  if (needsOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }

  // Default: creator dashboard
  return <Navigate to="/dashboard" replace />;
}

function AppContent() {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const hasAttemptedRef = useRef(false);

  // Track page views on route changes for Google Analytics
  usePageViewTracking();

  // Recover once when a stale client tries to load an old lazy chunk after deploy.
  // Handles both Vite preload errors and dynamic import() failures (React.lazy).
  useEffect(() => {
    const RELOAD_KEY = 'chunk-load-recovered';

    const shouldReload = () => {
      if (sessionStorage.getItem(RELOAD_KEY) === 'true') return false;
      sessionStorage.setItem(RELOAD_KEY, 'true');
      return true;
    };

    const handlePreloadError = (event: Event) => {
      event.preventDefault();
      if (shouldReload()) window.location.reload();
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const msg = String(event.reason?.message ?? event.reason ?? '').toLowerCase();
      const isChunkError =
        msg.includes('failed to fetch dynamically imported module') ||
        msg.includes('importing a module script failed') ||
        msg.includes('error loading dynamically imported module');
      if (isChunkError && shouldReload()) {
        event.preventDefault();
        window.location.reload();
      }
    };

    window.addEventListener('vite:preloadError', handlePreloadError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    return () => {
      window.removeEventListener('vite:preloadError', handlePreloadError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // Identify user in Sentry whenever session changes
  useEffect(() => {
    if (session?.user) {
      Sentry.setUser({ id: session.user.id, email: session.user.email });
    } else {
      Sentry.setUser(null);
    }
  }, [session]);

  useEffect(() => {
    setLoading(true);
    // Check initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    });

    // Listen for auth changes
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      // Only update session state for meaningful auth changes, not token refreshes
      // TOKEN_REFRESHED fires on tab focus and would cause unnecessary re-renders
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT' || event === 'USER_UPDATED') {
        setSession(session);
        setLoading(false);
      }
    });

    // Cleanup listener on component unmount
    return () => {
      authListener?.subscription?.unsubscribe();
    };
  }, []);

  // Persist intended destination for post-login redirect when logged out
  useEffect(() => {
    try {
      if (!session) {
        const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        // Avoid saving callback route itself or auth routes
        if (!current.startsWith('/auth')) {
          localStorage.setItem('return_to', current);
        }
        // If user is visiting a deep campaign link while logged out, remember the campaign id for focus later
        const match = window.location.pathname.match(/^\/?campaigns\/([0-9a-fA-F-]{10,})/);
        if (match && match[1]) {
          try { localStorage.setItem('campaign_focus', match[1]); } catch (_) {}
        }
      }
    } catch (_) {
      // ignore
    }
  }, [session]);

  // Capture referral code from URL and persist locally for post-auth attribution
  useEffect(() => {
    try {
      const searchParams = new URLSearchParams(window.location.search);
      const referralFromUrl = searchParams.get('ref');
      if (referralFromUrl && referralFromUrl.trim().length > 0) {
        fetch(`/api/referral/set?ref=${encodeURIComponent(referralFromUrl.trim())}`, { method: 'GET' }).catch(() => {});
        localStorage.setItem('videreo_ref', referralFromUrl.trim());
        searchParams.delete('ref');
        const nextSearch = searchParams.toString();
        const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash}`;
        window.history.replaceState(null, '', nextUrl);
      }
    } catch (e) {
      // no-op
    }
  }, []);

  // After auth, attempt to assign referrer once per user id
  useEffect(() => {
    const assignReferrerIfNeeded = async () => {
      if (!session?.user?.id) return;
      try {
        if (hasAttemptedRef.current) return;
        hasAttemptedRef.current = true;
        const referralCode = localStorage.getItem('videreo_ref');
        if (!referralCode) return;
        const appliedKey = `ref_applied_${session.user.id}`;
        if (localStorage.getItem(appliedKey) === 'true') return;

        const res = await fetch('/api/supabase/assign-referrer', {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': session?.access_token ? `Bearer ${session.access_token}` : ''
          },
          body: JSON.stringify({ profile_id: session.user.id, referral_code: referralCode })
        });
        if (res.ok) {
          localStorage.setItem(appliedKey, 'true');
          localStorage.removeItem('videreo_ref');
        } else {
          try {
            const data = await res.json();
            const msg = String(data?.error || '').toLowerCase();
            if (msg.includes('self-referral') || msg.includes('invalid referral code') || msg.includes('profile not found')) {
              localStorage.setItem(appliedKey, 'true');
            }
          } catch (_) {
            // ignore
          }
        }
      } catch (e) {
        // swallow to avoid disrupting UX
      }
    };

    assignReferrerIfNeeded();
  }, [session]);

  // After auth, attempt to save consent data once per user id
  useEffect(() => {
    const saveConsentIfNeeded = async () => {
      if (!session?.user?.id) return;
      try {
        const consentData = localStorage.getItem('videreo_consent');
        if (!consentData) return;
        const appliedKey = `consent_applied_${session.user.id}`;
        if (localStorage.getItem(appliedKey) === 'true') return;

        const parsedConsent = JSON.parse(consentData);
        const res = await fetch('/api/supabase/save-consent', {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': session?.access_token ? `Bearer ${session.access_token}` : ''
          },
          body: JSON.stringify({ 
            profile_id: session.user.id, 
            marketing_consent: parsedConsent.marketing_consent
          })
        });
        if (res.ok) {
          localStorage.setItem(appliedKey, 'true');
          localStorage.removeItem('videreo_consent');
        } else {
          try {
            const data = await res.json();
            const msg = String(data?.error || '').toLowerCase();
            if (msg.includes('profile not found') || msg.includes('forbidden')) {
              localStorage.setItem(appliedKey, 'true');
            }
          } catch (_) {
            // ignore
          }
        }
      } catch (e) {
        // swallow to avoid disrupting UX
      }
    };

    saveConsentIfNeeded();
  }, [session]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="App min-h-svh">
        <ErrorBoundary>
          <Routes>
          {/* ============ PUBLIC ROUTES ============ */}
          
          {/* Auth callback */}
          <Route path="auth/callback" element={
            <Suspense fallback={<LoadingSpinner />}>
              <AuthCallbackPage />
            </Suspense>
          } />
          
          {/* Brand/Agency specific auth pages */}
          <Route path="auth/brand" element={
            <Suspense fallback={<LoadingSpinner />}>
              <BrandAuthPage />
            </Suspense>
          } />
          <Route path="auth/agency" element={
            <Suspense fallback={<LoadingSpinner />}>
              <AgencyAuthPage />
            </Suspense>
          } />
          
          {/* Accept invite (works with or without auth) */}
          <Route path="auth/invite/:token" element={
            <Suspense fallback={<LoadingSpinner />}>
              <AcceptInvitePage />
            </Suspense>
          } />
          
          {/* Public testimonial submission (no auth required) */}
          <Route path="testimonial/:token" element={
            <Suspense fallback={<LoadingSpinner />}>
              <TestimonialPage />
            </Suspense>
          } />
          
          {/* Public client report - token-based access */}
          <Route path="report/client/:clientSlug" element={
            <Suspense fallback={<LoadingSpinner />}>
              <ClientReportPage />
            </Suspense>
          } />

          {/* ============ AUTHENTICATED ROUTES ============ */}
          
          {!session ? (
            // Not authenticated - show auth form for all other routes
            <Route path="*" element={
              <div className="min-h-svh bg-white dark:bg-gray-900 w-full">
                <AuthForm />
              </div>
            } />
          ) : (
            <>
              {/* Root redirect */}
              <Route path="/" element={<RootRedirect />} />
              
              {/* ============ ORGANIZATION ROUTES ============ */}
              <Route path="org">
                {/* Org selection */}
                <Route path="select" element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <OrgSelectPage />
                  </Suspense>
                } />
                
                {/* Create new org — disabled (CollabsMap transition).
                    Gated to the role-aware "registrations closed" note; org
                    creation now happens on partner.videreo.com. */}
                <Route path="new" element={<RegistrationsClosedScreen role="org" />} />
                
                {/* Organization-specific routes (protected by OrgGuard) */}
                <Route path=":slug/*" element={
                  <OrgGuard>
                    <OrgLayout>
                      <Suspense fallback={<LoadingSpinner />}>
                        <Routes>
                          <Route index element={<Navigate to="dashboard" replace />} />
                          <Route path="dashboard" element={<OrgDashboardPage />} />
                          <Route path="reports" element={<OrgReportsPage />} />
                          <Route path="campaigns" element={<OrgCampaignsPage />} />
                          <Route path="clients" element={<OrgClientsPage />} />
                          <Route path="creators" element={<OrgCreatorsPage />} />
                          <Route path="action-center" element={<OrgActionCenterPage />} />
                          <Route path="tasks" element={<Navigate to="action-center" replace />} />
                          <Route path="assignments" element={<Navigate to="action-center" replace />} />
                          <Route path="team" element={<OrgTeamPage />} />
                          <Route path="billing" element={<OrgBillingPage />} />
                          <Route path="settings" element={<OrgSettingsPage />} />
                          <Route path="help" element={<OrgHelpPage />} />
                          <Route path="*" element={<Navigate to="dashboard" replace />} />
                        </Routes>
                      </Suspense>
                    </OrgLayout>
                  </OrgGuard>
                } />
              </Route>
              
              {/* ============ ADMIN ROUTES ============ */}
              <Route path="admin/*" element={
                <RoleGuard role="admin">
                  <AdminLayout>
                    <Suspense fallback={<LoadingSpinner />}>
                      <Routes>
                        <Route index element={<Navigate to="dashboard" replace />} />
                        <Route path="dashboard" element={<AdminDashboardPage />} />
                        <Route path="users" element={<AdminUsersPage />} />
                        <Route path="organizations" element={<AdminOrganizationsPage />} />
                        <Route path="campaigns" element={<AdminCampaignsPage />} />
                        <Route path="clients" element={<AdminClientsPage />} />
                        <Route path="creators" element={<AdminCreatorsPage />} />
                        <Route path="reports" element={<AdminReportsPage />} />
                        <Route path="company-stats" element={<AdminCompanyStatsPage />} />
                        <Route path="creator-tools" element={<AdminDeliverablesPage />} />
                        <Route path="action-center" element={<AdminActionCenterPage />} />
                        <Route path="creator-opportunities" element={<AdminCreatorOpportunitiesPage />} />
                        <Route path="tasks" element={<Navigate to="action-center" replace />} />
                        <Route path="assignments" element={<Navigate to="action-center" replace />} />
                        <Route path="*" element={<Navigate to="dashboard" replace />} />
                      </Routes>
                    </Suspense>
                  </AdminLayout>
                </RoleGuard>
              } />
              
              {/* ============ ONBOARDING ROUTE (outside layout) ============ */}
              <Route path="onboarding" element={
                <Suspense fallback={<LoadingSpinner />}>
                  <OnboardingPage />
                </Suspense>
              } />

              {/* ============ CREATOR PORTAL ROUTES ============ */}
              <Route path="*" element={
                <GuideCreationProvider>
                  <Suspense fallback={<LoadingSpinner />}>
                    <CreatorLayout>
                      <Suspense fallback={<div className="p-6">Loading...</div>}>
                        <Routes>
                          <Route path="dashboard" element={<CreatorDashboard />} />
                          
                          {/* Report Routes */}
                          <Route path="report" element={<ReportPage />} />
                          <Route path="report/global" element={<GlobalReportPage />} />
                          
                          {/* Guide Routes */}
                          <Route path="guides">
                            <Route index element={<GuidesPage />} />
                            <Route path="create" element={<CreateGuidePage />} />
                            <Route path="edit/:guideId" element={<EditGuidePage />} />
                          </Route>

                          {/* Getting Started & Help */}
                          <Route path="getting-started" element={<GettingStartedPage />} />
                          <Route path="help" element={<CreatorHelpPage />} />
                          
                          {/* Campaigns Routes */}
                          <Route path="campaigns" element={<CampaignsPage />} />
                          <Route path="campaigns/:campaignId" element={<CampaignsPage />} />
                          
                          {/* Profile Route */}
                          <Route path="profile" element={<ProfilePage />} />
                          
                          {/* Portfolio Routes: list at /portfolios, single at /portfolios/:id/edit */}
                          <Route path="portfolios" element={<PortfolioPage />} />
                          <Route path="portfolios/:portfolioId/edit" element={<EditPortfolioPage />} />
                          
                          {/* Links Route */}
                          <Route path="links" element={<LinksPage />} />

                          {/* Action Center & Assignments Routes */}
                          <Route path="action-center" element={<CreatorActionCenterPage />} />
                          <Route path="assignments" element={<Navigate to="action-center" replace />} />
                          
                          {/* Catch all route - redirect to dashboard */}
                          <Route path="*" element={<Navigate to="/dashboard" replace />} />
                        </Routes>
                      </Suspense>
                    </CreatorLayout>
                  </Suspense>
                </GuideCreationProvider>
              } />
            </>
          )}
          </Routes>
        </ErrorBoundary>
      </div>
      {typeof document !== 'undefined' ? createPortal(<Toaster />, document.body) : <Toaster />}
    </>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <OrganizationProvider>
        <AppContent />
      </OrganizationProvider>
    </QueryClientProvider>
  );
}

export default App;
